import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { feedback } from "../../shared/events";
import { schemaBuilder } from "../../shared/services";
import { ApiException } from "../../shared/services/api/ApiException";
import {
  IDetalhePerfis,
  IFuncionalidade,
  IProduto,
  PermissoesService,
} from "../../shared/services/api/permissoes/PermissoesService";
import { IVFormErros, useVFormRef } from "../../shared/forms";
import { AccordionNomePerfil } from "./componentes/AccordionNomePerfil";
import { AccordionPermissoes } from "./componentes/AccordionPermissoes";
import { changeTitle } from "../../shared/events";
import { useTemPermissao } from "../../shared/contexts/PerfilDeUsuarioContext";
import { GvdasaConfirmProvider } from "@gvdasa/components-confirm";
import {
  Box,
  FormControl,
  Grid,
  FooterActions,
  FooterAction,
  TextField,
} from "@gvdasa/react-components";

const formValidationShema: schemaBuilder.SchemaOf<IDetalhePerfis | any> =
  schemaBuilder.object().shape({
    Id: schemaBuilder.number().notRequired(),
    Nome: schemaBuilder.string().trim().required(),
  });

export const DetalhePermissoesPage: React.FC = () => {
  const { id = "novo" } = useParams<"id">();
  const formRef = useVFormRef();

  const deepCloneProdutos = (produtos: IProduto[]): IProduto[] => {
    if (typeof structuredClone === "function") {
      return structuredClone(produtos);
    }
    return JSON.parse(JSON.stringify(produtos));
  };

  const [isLoading, setIsLoading] = useState(true);
  const [produtos, setProdutos] = useState<IProduto[]>([]);

  const [Nome, setNome] = useState<string>("");
  const [erroPerfil, setErroPerfil] = useState<boolean>(false);
  const [textoDeErroNome, setTextoDeErroNome] = useState("");

  const [erroAoSalvarPermissoes, setErroAoSalvarPermissoes] =
    useState<boolean>(false);
  const [mensagemDeErroPermissoes, setMensagemDeErroPermissoes] = useState("");

  const [pesquisar, setPesquisar] = useState<string>("");
  const filtrosPorOperacao = produtos.flatMap((pr) =>
    pr.modulos.flatMap((m) =>
      m.funcionalidades.flatMap((f) =>
        f.operacoes.filter((op) =>
          op.nome.toLowerCase().startsWith(pesquisar.toLowerCase()),
        ),
      ),
    ),
  );
  const [filtrado, setFiltrado] = useState(false);

  const [finalState, setFinalState] = useState({
    nome: Nome,
    produtos: produtos,
  });

  const [initialValues, setInitialValues] = useState<{
    nome: string;
    produtos: IProduto[];
  }>({
    nome: "",
    produtos: [],
  });
  useEffect(() => {
    setFinalState({
      nome: Nome,
      produtos: produtos,
    });
  }, [Nome, produtos]);

  useEffect(() => {
    changeTitle("Permissões");

    return () => {
      changeTitle("");
    };
  }, []);

  const permitirGerenciarPermissoes = useTemPermissao(
    "login-service.geral.perfil.gerenciar-permissoes",
  );
  const permitirIncluirPerfil = useTemPermissao(
    "login-service.geral.perfil.incluir",
  );
  const permitirEditarPerfil = useTemPermissao(
    "login-service.geral.perfil.editar",
  );

  useEffect(() => {
    setIsLoading(true);
    if (id === "novo") {
      PermissoesService.getPermissoes(0).then((data) => {
        if (data instanceof ApiException) {
          setIsLoading(true);
          feedback(data.message, "error");
        } else {
          setIsLoading(false);
          setProdutos(data.data);
          setInitialValues({
            nome: "",
            produtos: deepCloneProdutos(data.data),
          });

          const dados = data.data.map((e) => e.modulos);
          const funcionalidades = dados.flatMap((e) =>
            e.flatMap((d) => d.funcionalidades),
          );
          const listaDePermissoesTrue: string[] = [];
          funcionalidades.map((item) => {
            item.operacoes.map((operacao) => {
              if (operacao.temPermissao === true) {
                listaDePermissoesTrue.push(`${item.id}${operacao.id}`);
              }
            });
          });
        }
      });
    } else {
      setIsLoading(true);
      PermissoesService.getPerfisById(id).then((data) => {
        setIsLoading(false);
        if (data instanceof ApiException) {
          feedback(data.message, "error");
          setIsLoading(true);
          setErroPerfil(true);
        } else {
          setIsLoading(true);
          setErroPerfil(false);
          const nomeOriginal = data.Nome;
          setNome(nomeOriginal);
          PermissoesService.getPermissoes(id).then((data) => {
            if (data instanceof ApiException) {
              feedback(data.message, "error");
            } else {
              setIsLoading(false);
              setProdutos(data.data);
              setInitialValues({
                nome: nomeOriginal,
                produtos: deepCloneProdutos(data.data),
              });

              const dados = data.data.map((e) => e.modulos);
              const funcionalidades = dados.flatMap((e) =>
                e.flatMap((d) => d.funcionalidades),
              );
              const listaDePermissoesTrue: string[] = [];
              funcionalidades.map((item) => {
                item.operacoes.map((operacao) => {
                  if (operacao.temPermissao === true) {
                    listaDePermissoesTrue.push(`${item.id}${operacao.id}`);
                  }
                });
              });
            }
          });
        }
      });
    }
    return () => {
      setIsLoading(false);
    };
  }, [id]);

  useEffect(() => {
    if (pesquisar.length > 0) {
      setFiltrado(true);
    }
    if (pesquisar.length === 0) {
      setFiltrado(false);
    }
  }, [pesquisar]);

  const handleMarcarTodos = (
    e: React.ChangeEvent<HTMLInputElement>,
    modulo: {
      id: string;
      nome: string;
      funcionalidades: IFuncionalidade[];
    },
    produto: IProduto,
  ) => {
    const indeterminate = e.target.attributes[2].nodeValue;
    const lista = produto.modulos.flatMap((m) =>
      m.funcionalidades.flatMap((f) =>
        f.operacoes.filter(
          (op) =>
            op.temPermissao === true &&
            op.nome.toLowerCase().startsWith(pesquisar.toLowerCase()),
        ),
      ),
    );

    // Criar nova cópia dos produtos
    const novosProdutos = produtos.map((prod) => {
      if (prod.idProduto === produto.idProduto) {
        return {
          ...prod,
          modulos: prod.modulos.map((mod) => {
            if (mod.id === modulo.id) {
              return {
                ...mod,
                funcionalidades: mod.funcionalidades.map((func) => ({
                  ...func,
                  operacoes: func.operacoes.map((op) => {
                    const deveAlterar = filtrosPorOperacao.some(
                      (fo) => fo.id === op.id,
                    );
                    if (deveAlterar) {
                      if (
                        e.target.checked === true &&
                        indeterminate === "true"
                      ) {
                        const listaTrue = lista.filter(
                          (t) => t.temPermissao === true,
                        );
                        const listaFalse = lista.filter(
                          (t) => t.temPermissao === false,
                        );
                        return {
                          ...op,
                          temPermissao:
                            listaTrue.length > listaFalse.length ? false : true,
                        };
                      } else {
                        return {
                          ...op,
                          temPermissao: e.target.checked,
                        };
                      }
                    }
                    return op;
                  }),
                })),
              };
            }
            return mod;
          }),
        };
      }
      return prod;
    });

    setProdutos(novosProdutos);
  };

  const handleValoresCheckbox = (
    event: any,
    data: IFuncionalidade,
    idOperacao: string,
  ) => {
    // Criar nova cópia dos produtos
    const novosProdutos = produtos.map((produto) => ({
      ...produto,
      modulos: produto.modulos.map((modulo) => ({
        ...modulo,
        funcionalidades: modulo.funcionalidades.map((func) => {
          if (func.id === data.id) {
            return {
              ...func,
              operacoes: func.operacoes.map((operacao) => {
                if (operacao.id === idOperacao) {
                  return {
                    ...operacao,
                    temPermissao: event.target.checked,
                  };
                }
                return operacao;
              }),
            };
          }
          return func;
        }),
      })),
    }));

    setProdutos(novosProdutos);
  };

  const handleSubmit = useCallback(() => {
    feedback("Salvando...", "info");

    formValidationShema
      .validate({ id, Nome }, { abortEarly: false })
      .then((dadosValidados: any) => {
        if (!dadosValidados) {
          feedback("Campos inválidos.", "error");
          return;
        }
        if (id === "novo") {
          PermissoesService.createPerfis(dadosValidados).then((result) => {
            if (result instanceof ApiException) {
              feedback(result.message, "error");
              setErroPerfil(true);
              setTextoDeErroNome(result.message);
            } else {
              setErroPerfil(false);
              feedback("Salvo com sucesso!", "success");
              PermissoesService.createPermissoes({
                idPerfil: result.Id,
                produtos: produtos,
              }).then((data) => {
                if (data instanceof ApiException) {
                  feedback(data.message, "error");
                  setErroAoSalvarPermissoes(true);
                  setMensagemDeErroPermissoes(data.message);
                } else {
                  setErroAoSalvarPermissoes(false);
                  singleSpaNavigate("/login-service/perfis");
                }
              });
            }
          });
        } else {
          PermissoesService.updatePerfisById(id, dadosValidados).then(
            (result) => {
              if (result instanceof ApiException) {
                feedback(result.message, "error");
                setErroPerfil(true);
                setTextoDeErroNome(result.message);
              } else {
                setErroPerfil(false);
                feedback(" Perfil salvo com sucesso!", "success");
                PermissoesService.createPermissoes({
                  idPerfil: id,
                  produtos: produtos,
                }).then((result) => {
                  if (result instanceof ApiException) {
                    feedback(result.message, "error");
                    setErroAoSalvarPermissoes(true);
                    setMensagemDeErroPermissoes(result.message);
                  } else {
                    setErroAoSalvarPermissoes(false);
                    singleSpaNavigate("/login-service/perfis");
                  }
                });
              }
            },
          );
        }
      })
      .catch((err: schemaBuilder.ValidationError) => {
        feedback("Campos inválidos.", "error");
        const validationErrors: IVFormErros = {};

        err.inner.forEach((error: any) => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
          setErroPerfil(true);
          setTextoDeErroNome(error.message);
        });
        formRef.current?.setErrors(validationErrors);
      });
  }, [formRef, id, Nome, produtos]);

  return (
    <Box display="flex" gap={4} flexDirection="column" height="auto">
      <Grid container spacing={2} alignItems="baseline">
        <Grid size={12}>
          <FormControl
            data-testid="teste-accordion-nome"
            required
            margin="none"
            fullWidth
          >
            <AccordionNomePerfil
              disabled={
                id === "novo" ? permitirIncluirPerfil : permitirEditarPerfil
              }
              Nome={Nome}
              isLoading={isLoading}
              setNome={setNome}
              textoDeErroNome={textoDeErroNome}
              erroPerfil={erroPerfil}
            />
          </FormControl>
        </Grid>
        <Grid size={12}>
          <FormControl
            margin="none"
            data-testid="teste-accordion-produtos"
            fullWidth
            error={erroAoSalvarPermissoes}
          >
            <AccordionPermissoes
              disabled={permitirGerenciarPermissoes}
              isLoading={isLoading}
              produtos={produtos}
              erroAoSalvarPermissoes={erroAoSalvarPermissoes}
              handleValoresCheckbox={handleValoresCheckbox}
              mensagemDeErroPermissoes={mensagemDeErroPermissoes}
              handleMarcarTodos={handleMarcarTodos}
              pesquisar={pesquisar}
              setPesquisar={setPesquisar}
              filtrosPorOperacao={filtrosPorOperacao}
              setFiltrado={setFiltrado}
              filtrado={filtrado}
            />
          </FormControl>
        </Grid>
        {!isLoading && (
          <GvdasaConfirmProvider
            initialState={initialValues}
            finalState={finalState}
          />
        )}
      </Grid>

      <FooterActions>
        <FooterAction
          id="gv-navigate"
          variant="outlined"
          size="medium"
          type="button"
          onClick={() => singleSpaNavigate("/login-service/perfis")}
        >
          Voltar
        </FooterAction>
        <FooterAction
          variant="contained"
          type="submit"
          size="medium"
          onClick={() => {
            handleSubmit();
          }}
        >
          Salvar e voltar
        </FooterAction>
      </FooterActions>
    </Box>
  );
};
